import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, Row, Tooltip, Spin } from 'antd';
import Translate from 'react-translate-component';
import { contains, filter, path, prop, sum, find } from 'ramda';

import withAutoFocusForm from 'components/hocs/withAutoFocusForm';
import { EVENT_RESULT_TYPES } from 'constants/events';
import EventResultMetaForm from '../result/EventResultMetaForm';
import FreeForm from '../result/forms/FreeForm';
import XMLForm from '../result/forms/XMLForm';
import FileUploader from '../result/forms/FileUploader';

const { FREE, FILE, FORM, SCRIPT } = EVENT_RESULT_TYPES;

const getTranslation = (isTookPlace, resultNotSelected) => resultNotSelected ? (
    <Translate content={isTookPlace ? 'events.fillResult' : 'events.fillReason'} />
) : null;

const countAllowedDocuments = event => sum([
    !!prop(FREE, event.result),
    !!(prop(FORM, event.result) || prop(SCRIPT, event.result)),
    !!prop(FILE, event.result)
]);

const isMoreThanOneAllowed = event => countAllowedDocuments(event) > 1;

const getSingleType = event => find(type => prop(type, event.result), [FREE, FORM, FILE]) || FORM;

@withAutoFocusForm
export default class EventResultForm extends Component {
    static propTypes = {
        event: PropTypes.object.isRequired,
        values: PropTypes.object.isRequired,
        form: PropTypes.object.isRequired,
        type: PropTypes.string.isRequired,
        formAction: PropTypes.object.isRequired,
        formValues: PropTypes.object.isRequired,
        handleSubmit: PropTypes.func.isRequired,
    }

    state = {
        resultType: null,
    }

    componentDidMount() {
        const initialResultType = isMoreThanOneAllowed(this.props.event) ? null : getSingleType(this.props.event);
        this.setResultType(initialResultType);

        this.props.form.change('result.type', this.props.type);
    }

    active = (type) => this.state.resultType === type;

    setResultType = (resultType) => {
        const newResultType = this.state.resultType === resultType ? null : resultType;

        this.setState({ resultType: newResultType });
        this.props.form.change('result.resultType', newResultType);
    };

    isSaveReady = () => {
        const { event : { result: { eventWithScript }} } = this.props;
        const { resultType } = this.state;

        return resultType && !(resultType === FORM && eventWithScript);
    }

    getResultButton = (resultType) => (
        <Button onClick={() => this.setResultType(resultType)} className={cx({ active: this.active(resultType) })}>
            <Translate content={`events.${resultType}`} />
        </Button>
    );

    getShortResults = (evaluatedScore, shortResults = []) => {
        return evaluatedScore ? (
            filter(result => contains(evaluatedScore, result.evaluatedScores), shortResults)
        ) : shortResults;
    }

    onSubmit = ({ result, file, shortResultBlock }) => {
        this.sendData(result || file, shortResultBlock, false);
    }

    onSubmitOnlyShort = ({ result, shortResultBlock }) => {
        this.sendData(result, shortResultBlock, true);
    }

    onSubmitWithScript = result => {
        this.sendData(result, this.props.formValues.shortResultBlock, false);
    }

    render () {
        const { resultType } = this.state;
        const { form: { change }, values, event } = this.props;
        const { formAction: { meta: { pending } } } = this.props;

        const tookPlace = path(['shortResultBlock', 'tookPlace'], values);
        const evaluatedScore = path(['shortResultBlock', 'evaluatedScore'], values);
        const resultCode = path(['shortResultBlock', 'resultCode'], values);
        const resultNotSelected = !resultCode;

        const shortResults = path(['shortResults'], event);
        const abortedResultReasons = path(['abortedResultReasons'], event);
        const shortResultBlock = path(['result', 'shortResultBlock'], event);
        const shortResult = path(['result', 'shortResult'], event);

        const resultTypeChoosen = !!this.state.resultType;
        const shortBlockForbidden = !shortResultBlock;
        const isTookPlace = tookPlace === 1;

        const moreThanOneAllowed = isMoreThanOneAllowed(event);

        return (
            <Spin spinning={pending}>
                <div className='event-modal card-form panel-view' noValidate>
                    { shortResultBlock &&
                        <EventResultMetaForm
                            shortResults={this.getShortResults(evaluatedScore, shortResults)}
                            abortedResultReasons={abortedResultReasons}
                            tookPlace={tookPlace}
                            isTookPlace={isTookPlace}
                            evaluatedScore={evaluatedScore}
                            resultCode={resultCode}
                            change={change}
                        />
                    }
                    { (tookPlace || shortBlockForbidden) &&
                        <div>
                            <Row
                                className={cx('card-bottom event-buttons', { 'empty-event-buttons': !moreThanOneAllowed })}
                            >
                                { shortResult && shortResultBlock &&
                                    <Tooltip title={getTranslation(isTookPlace, resultNotSelected)} placement="top">
                                        <Button
                                            onClick={() => this.props.handleSubmit()}
                                            disabled={(resultTypeChoosen && moreThanOneAllowed) || resultNotSelected}
                                        >
                                            <Translate content="events.saveShort" />
                                        </Button>
                                    </Tooltip>
                                }
                                { moreThanOneAllowed &&
                                    <Button.Group className={shortResultBlock ? 'pull-right' : 'pull-left'}>
                                        { event.result[FREE] && this.getResultButton(FREE) }
                                        { (event.result[FORM] || event.result[SCRIPT]) && this.getResultButton(FORM) }
                                        { event.result[FILE] && this.getResultButton(FILE) }
                                    </Button.Group>
                                }
                            </Row>

                            { resultType === FORM && (
                                <XMLForm {...this.props} />
                            )}
                            { resultType === FILE && <FileUploader required {...this.props} /> }
                            { resultType === FREE && <FreeForm {...this.props} /> }

                            { resultType && (
                                <Row className="card-bottom m-t-30">
                                    <Button type='blue' onClick={() => this.props.handleSubmit()}>
                                        <Translate content="modal_settings.save" />
                                    </Button>
                                </Row>
                            )}
                        </div>
                    }
                </div>
            </Spin>
        );
    }
}
